/**
 * Sesión del portal: quién entró, con qué rol y con qué credencial.
 *
 * DOS CREDENCIALES, UNA SOLA REGLA
 * --------------------------------
 * El portal puede hablarle al API de dos maneras:
 *  1. **Sesión de usuario** (`POST /api/auth/login`): token opaco y REVOCABLE,
 *     atado a una persona del padrón y a su rol. Es el camino normal.
 *  2. **Bearer manual** de Ajustes (`SCOUT_API_TOKEN`): el token único de la
 *     federación, anterior a los usuarios. Queda como MODO DE COMPATIBILIDAD
 *     para integraciones (un tablero embebido, un script) y NO se elimina.
 * La sesión gana siempre que exista: `effectiveToken()` es el único lugar donde
 * se decide, así que nadie más tiene que acordarse de la precedencia.
 *
 * QUÉ ES LA VERDAD
 * ----------------
 * Lo que guarda el navegador es la CREDENCIAL, no la identidad: el rol y el
 * estado los dice `GET /api/auth/me` en cada arranque. Si a alguien lo bajaron
 * de admin a viewer, o le revocaron la sesión, el portal se entera por el API y
 * no por lo que quedó escrito en el localStorage.
 *
 * Todo este módulo es puro (no importa React) para poder testearlo en Node.
 */
import type { AuthVia, Identity, PublicUser, Role } from './api-client';
import { isRole } from './api-client';
import type { MessageKey, TFunction } from '../i18n/index';
import { defaultKeyValueStore, type KeyValueStore } from './settings';

// ---------------------------------------------------------------------------
// La sesión guardada
// ---------------------------------------------------------------------------

/**
 * Lo que persiste el portal después de un login exitoso.
 *
 * El API no publica el vencimiento del token (lo maneja el server, 30 días por
 * defecto), así que acá no hay `expiresAt` que mentir: la sesión vale hasta que
 * el API conteste 401.
 */
export interface PortalSession {
  token: string;
  user: { id: string; username: string; name: string; role: Role };
  /** Cuándo se obtuvo, para poder mostrar "sesión iniciada el …". */
  since: string;
}

export const SESSION_KEY = 'scout.portal.session';

/** Cualquier cosa leída del storage → sesión válida, o `null`. */
export function normalizeSession(raw: unknown): PortalSession | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const obj = raw as Record<string, unknown>;
  const token = typeof obj['token'] === 'string' ? obj['token'].trim() : '';
  const user = (typeof obj['user'] === 'object' && obj['user'] !== null ? obj['user'] : {}) as Record<
    string,
    unknown
  >;
  const username = typeof user['username'] === 'string' ? user['username'] : '';
  if (token === '' || username === '') return null;
  return {
    token,
    user: {
      id: typeof user['id'] === 'string' ? user['id'] : username,
      username,
      name: typeof user['name'] === 'string' && user['name'] !== '' ? user['name'] : username,
      role: isRole(user['role']) ? user['role'] : 'viewer',
    },
    since: typeof obj['since'] === 'string' ? obj['since'] : '',
  };
}

export function loadSession(store: KeyValueStore = defaultKeyValueStore()): PortalSession | null {
  try {
    const raw = store.getItem(SESSION_KEY);
    if (!raw) return null;
    return normalizeSession(JSON.parse(raw));
  } catch {
    // Storage corrupto o bloqueado: se trabaja sin sesión, no se rompe el portal.
    return null;
  }
}

export function saveSession(
  session: PortalSession,
  store: KeyValueStore = defaultKeyValueStore(),
): void {
  try {
    store.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Modo privado / cuota llena: la sesión vive sólo en memoria (hasta el F5).
  }
}

export function clearSession(store: KeyValueStore = defaultKeyValueStore()): void {
  try {
    store.removeItem(SESSION_KEY);
  } catch {
    /* no-op */
  }
}

/** Login del API → sesión persistible. */
export function sessionFromLogin(
  result: { token: string; user: PortalSession['user'] },
  now: string = new Date().toISOString(),
): PortalSession {
  return { token: result.token, user: { ...result.user }, since: now };
}

/**
 * Store observable mínimo para `useSyncExternalStore` (mismo patrón que
 * `SettingsStore`: el estado vive fuera de React).
 */
export class SessionStore {
  private snapshot: PortalSession | null;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly kv: KeyValueStore = defaultKeyValueStore()) {
    this.snapshot = loadSession(kv);
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => void this.listeners.delete(listener);
  };

  readonly getSnapshot = (): PortalSession | null => this.snapshot;

  readonly set = (session: PortalSession): PortalSession => {
    this.snapshot = session;
    saveSession(session, this.kv);
    this.emit();
    return session;
  };

  readonly clear = (): void => {
    this.snapshot = null;
    clearSession(this.kv);
    this.emit();
  };

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

// ---------------------------------------------------------------------------
// Precedencia de credenciales
// ---------------------------------------------------------------------------

/** De dónde salió el Bearer que el portal está mandando. */
export type TokenSource = 'session' | 'manual' | 'none';

export interface EffectiveToken {
  token: string;
  source: TokenSource;
}

/**
 * Qué credencial viaja en el header. La sesión gana sobre el Bearer manual: si
 * alguien se logueó, sus permisos son los que valen, no los del token pegado a
 * mano (que es de la federación entera y suele ser admin).
 */
export function effectiveToken(
  session: PortalSession | null,
  manualToken: string,
): EffectiveToken {
  const fromSession = session?.token.trim() ?? '';
  if (fromSession !== '') return { token: fromSession, source: 'session' };
  const manual = manualToken.trim();
  if (manual !== '') return { token: manual, source: 'manual' };
  return { token: '', source: 'none' };
}

// ---------------------------------------------------------------------------
// Etiquetas
// ---------------------------------------------------------------------------

const ROLE_KEYS: Record<Role, MessageKey> = {
  admin: 'auth.roles.admin',
  scout: 'auth.roles.scout',
  coach: 'auth.roles.coach',
  viewer: 'auth.roles.viewer',
};

/**
 * Nombre del rol EN EL VOCABULARIO DEL CLIENTE (estadístico, entrenador,
 * dirigencia). El id técnico (`scout`, `viewer`) se usa cuando hay que hablarle
 * al API o pedirle un permiso a la federación; en pantalla va este.
 */
export function roleLabel(role: Role, t: TFunction): string {
  return t(ROLE_KEYS[role]);
}

const VIA_KEYS: Record<AuthVia, MessageKey> = {
  session: 'auth.viaSession',
  'legacy-token': 'auth.viaLegacyToken',
  open: 'auth.viaOpen',
  'match-credential': 'auth.viaMatchCredential',
};

/** Con qué credencial se está entrando (sesión / token compat / API abierto). */
export function viaLabel(via: AuthVia, t: TFunction): string {
  return t(VIA_KEYS[via]);
}

const VIA_TITLE_KEYS: Partial<Record<AuthVia, MessageKey>> = {
  'legacy-token': 'auth.viaLegacyTokenTitle',
  open: 'auth.viaOpenTitle',
  'match-credential': 'auth.viaMatchCredentialTitle',
};

/** Explicación larga (tooltip) para los dos modos que NO son una sesión real. */
export function viaWarning(via: AuthVia, t: TFunction): string | null {
  const key = VIA_TITLE_KEYS[via];
  return key === undefined ? null : t(key);
}

// ---------------------------------------------------------------------------
// Rol → qué se puede hacer en el portal
// ---------------------------------------------------------------------------

/**
 * El padrón es cosa de `admin` (el API lo aplica con el permiso `users:manage`;
 * acá sólo se evita ofrecer una pantalla que va a rebotar con 403).
 *
 * OJO con `legacy-token` y `open`: el API los trata como admin sintético, así
 * que la pantalla de usuarios TAMBIÉN les funciona. Es correcto —son el modo
 * compatibilidad y el modo dev— y por eso la condición mira el rol, no el `via`.
 */
export function canManageUsers(identity: Identity | null): boolean {
  return identity !== null && identity.role === 'admin';
}

/**
 * ¿Puede armar torneos? El API pide `competitions:manage`, que hoy tiene sólo
 * el `admin` (el área de competencia de la federación). Vale la misma nota que
 * arriba sobre el token legacy y el modo dev.
 *
 * La pantalla igual se muestra completa para todos: lo que esto esconde son
 * los BOTONES de generar fixture y enganchar partidos, que sin el permiso
 * rebotarían con 403.
 */
export function canManageCompetitions(identity: Identity | null): boolean {
  return identity !== null && identity.role === 'admin';
}

/** Permiso que el API pide para armar el torneo (se le nombra al usuario tal cual). */
export const COMPETITIONS_PERMISSION = 'competitions:manage';

/**
 * ¿Puede importar `.dvw` histórico? El API pide `events:write`, que tienen el
 * `admin` y el `scout` (es la misma escritura del log que hace la captura, con
 * otro origen). El `coach` y el `viewer` no: para ellos la pantalla ni se
 * ofrece en la barra.
 */
export function canImportDvw(identity: Identity | null): boolean {
  return identity !== null && (identity.role === 'admin' || identity.role === 'scout');
}

/**
 * ¿Y además puede poblar la base de equipos? Eso es `teams:write` (sólo
 * `admin`). No es un detalle de UI: sin ese permiso el import entra igual y
 * deja los planteles como están, y la pantalla tiene que decirlo ANTES de que
 * el operador se pregunte por qué no aparecieron los equipos.
 */
export function canImportTeams(identity: Identity | null): boolean {
  return identity !== null && identity.role === 'admin';
}

/** Los dos permisos del import, nombrados como los pide el API. */
export const IMPORT_PERMISSION = 'events:write';
export const TEAMS_PERMISSION = 'teams:write';

/** Estado de la pantalla de usuarios ANTES de pedir nada al API. */
export type UsersAccess =
  /** Nadie logueado y sin credencial: hay que ingresar. */
  | 'anon'
  /** Logueado, pero el rol no llega: se explica, no se reintenta. */
  | 'forbidden'
  | 'ok';

export function usersAccess(identity: Identity | null): UsersAccess {
  if (identity === null) return 'anon';
  return identity.role === 'admin' ? 'ok' : 'forbidden';
}

/** Permiso que el API pide para el padrón (se le nombra al usuario tal cual). */
export const USERS_PERMISSION = 'users:manage';

/**
 * ¿Este usuario se está por borrar a sí mismo? El API lo rechaza con 409, pero
 * es mejor no ofrecer el botón que explicar el error después.
 */
export function isSelf(identity: Identity | null, user: PublicUser): boolean {
  return identity !== null && identity.via === 'session' && identity.username === user.username;
}

// ---------------------------------------------------------------------------
// Estado de la identidad y puerta de login
// ---------------------------------------------------------------------------

/** Lo que sabe el portal sobre quién es, según le contestó `GET /api/auth/me`. */
export type IdentityStatus =
  /** Todavía preguntando. */
  | 'checking'
  /** El API contestó: hay identidad (sesión, token de compatibilidad o dev). */
  | 'ok'
  /** El API contestó 401: no hay credencial válida. */
  | 'anon'
  /**
   * No se pudo saber (no hay red, la URL del API está mal, contestó cualquier
   * cosa). NO es lo mismo que "no estás autenticado": acá no se bloquea nada,
   * porque el problema es de conexión y la pantalla de login no lo arregla.
   */
  | 'unknown';

/**
 * ¿Se muestra el login en lugar de la ruta pedida?
 *
 * Sólo con un 401 explícito del API. Dos excepciones a propósito:
 *  - `#/login`, obvio;
 *  - `#/settings`, porque si el portal apunta al API equivocado la salida es
 *    corregir la URL, y encerrar al usuario en un login que va a fallar es la
 *    forma más rápida de dejarlo sin herramientas.
 */
export function gateToLogin(status: IdentityStatus, path: string): boolean {
  if (status !== 'anon') return false;
  return path !== LOGIN_HASH && path !== '#/settings';
}

// ---------------------------------------------------------------------------
// Formulario del padrón (mismas reglas que valida el API, dichas antes)
// ---------------------------------------------------------------------------

/** Igual que `USERNAME_PATTERN` del API: es la clave del padrón y va en la URL. */
export const USERNAME_PATTERN = /^[a-z0-9._-]{1,64}$/;

/** Mismo mínimo que `MIN_PASSWORD_LENGTH` en apps/api/src/store/user-store.ts. */
export const MIN_PASSWORD_LENGTH = 8;

export interface UserFormValues {
  username: string;
  name: string;
  role: Role;
  /** Vacío al editar = no se cambia la contraseña. */
  password: string;
  disabled: boolean;
}

export const EMPTY_USER_FORM: UserFormValues = {
  username: '',
  name: '',
  role: 'scout',
  password: '',
  disabled: false,
};

export type UserPayload =
  | { ok: true; username: string; body: { name: string; role: Role; password?: string; disabled: boolean } }
  | { ok: false; errors: string[] };

/**
 * Formulario → cuerpo del PUT, o la lista de errores traducidos.
 *
 * Las reglas son EXACTAMENTE las del API (username saneado y en minúsculas,
 * nombre obligatorio, contraseña de 8+ y obligatoria sólo al crear). Repetirlas
 * acá no es duplicar la autoridad —el API sigue validando— sino no gastar un
 * request para decirle a alguien que le falta el nombre.
 */
export function buildUserPayload(
  form: UserFormValues,
  creating: boolean,
  t: TFunction,
): UserPayload {
  const errors: string[] = [];
  const username = form.username.trim().toLowerCase();
  const name = form.name.trim();

  if (username === '') errors.push(t('users.errors.usernameRequired'));
  else if (!USERNAME_PATTERN.test(username)) errors.push(t('users.errors.usernameInvalid'));
  if (name === '') errors.push(t('users.errors.nameRequired'));
  if (creating && form.password === '') errors.push(t('users.errors.passwordRequired'));
  else if (form.password !== '' && form.password.length < MIN_PASSWORD_LENGTH) {
    errors.push(t('users.errors.passwordShort', { min: MIN_PASSWORD_LENGTH }));
  }

  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    username,
    body: {
      name,
      role: form.role,
      // Sin contraseña el API conserva la vigente (y no revoca sesiones).
      ...(form.password !== '' ? { password: form.password } : {}),
      disabled: form.disabled,
    },
  };
}

// ---------------------------------------------------------------------------
// Rutas nuevas
// ---------------------------------------------------------------------------

export const LOGIN_HASH = '#/login';
export const USERS_HASH = '#/users';

/**
 * `#/login` y `#/users` resueltas desde el `path` YA normalizado del router.
 *
 * Por qué acá y no en `src/lib/router.ts`: ese archivo es de otra zona en esta
 * tanda de trabajo. Cuando se lo pueda tocar, estas dos filas se mudan al
 * `switch` de `parseHash` y esta función se borra sin tocar nada más (App.tsx
 * ya la consulta en un solo lugar).
 */
export function authRoute(path: string): 'login' | 'users' | null {
  if (path === LOGIN_HASH) return 'login';
  if (path === USERS_HASH) return 'users';
  return null;
}
